import { __assign } from 'tslib';
import React, { forwardRef, useMemo, useState, useRef, useEffect, useImperativeHandle } from 'react';
import { use100vh } from 'react-div-100vh';
import mergeRefs from 'react-merge-refs';
import { useSwipeable } from 'react-swipeable';
import cn from 'classnames';
import { BaseModal } from '../../base-modal/esm';
import { fnUtils, getDataTestId } from '../../shared/esm';
import { Footer } from './components/footer/Component.js';
import { Header } from './components/header/Component.js';
import { SwipeableBackdrop } from './components/swipeable-backdrop/Component.js';
import { convertPercentToNumber, TIMEOUT, MARKER_HEIGHT, SCROLL_OFFSET, SWIPE_VELOCITY, CLOSE_OFFSET } from './utils.js';
import '../../navigation-bar/esm';
import '../../backdrop/esm';

var styles = {"modal":"bottom-sheet__modal_1fh7w","wrapper":"bottom-sheet__wrapper_1fh7w","fullscreen":"bottom-sheet__fullscreen_1fh7w","component":"bottom-sheet__component_1fh7w","withTransition":"bottom-sheet__withTransition_1fh7w","scrollableContainer":"bottom-sheet__scrollableContainer_1fh7w","marker":"bottom-sheet__marker_1fh7w","defaultMarker":"bottom-sheet__defaultMarker_1fh7w","content":"bottom-sheet__content_1fh7w","noHeader":"bottom-sheet__noHeader_1fh7w","noFooter":"bottom-sheet__noFooter_1fh7w","scrollLocked":"bottom-sheet__scrollLocked_1fh7w","hiddenScrollbar":"bottom-sheet__hiddenScrollbar_1fh7w","disabledPointerEvents":"bottom-sheet__disabledPointerEvents_1fh7w","appear":"bottom-sheet__appear_1fh7w","enter":"bottom-sheet__enter_1fh7w","appearActive":"bottom-sheet__appearActive_1fh7w","enterActive":"bottom-sheet__enterActive_1fh7w","enterDone":"bottom-sheet__enterDone_1fh7w","appearDone":"bottom-sheet__appearDone_1fh7w","exit":"bottom-sheet__exit_1fh7w","exitActive":"bottom-sheet__exitActive_1fh7w","background-accent":"bottom-sheet__background-accent_1fh7w","background-info":"bottom-sheet__background-info_1fh7w","background-attention-muted":"bottom-sheet__background-attention-muted_1fh7w","background-positive-muted":"bottom-sheet__background-positive-muted_1fh7w","background-negative-muted":"bottom-sheet__background-negative-muted_1fh7w","background-primary":"bottom-sheet__background-primary_1fh7w","background-primary-inverted":"bottom-sheet__background-primary-inverted_1fh7w","background-secondary":"bottom-sheet__background-secondary_1fh7w","background-secondary-inverted":"bottom-sheet__background-secondary-inverted_1fh7w","background-tertiary":"bottom-sheet__background-tertiary_1fh7w","background-tertiary-inverted":"bottom-sheet__background-tertiary-inverted_1fh7w","background-quaternary":"bottom-sheet__background-quaternary_1fh7w","background-quaternary-inverted":"bottom-sheet__background-quaternary-inverted_1fh7w","background-specialbg-component":"bottom-sheet__background-specialbg-component_1fh7w","background-specialbg-component-inverted":"bottom-sheet__background-specialbg-component-inverted_1fh7w","background-specialbg-primary-grouped":"bottom-sheet__background-specialbg-primary-grouped_1fh7w","background-specialbg-secondary-grouped":"bottom-sheet__background-specialbg-secondary-grouped_1fh7w","background-specialbg-tertiary-grouped":"bottom-sheet__background-specialbg-tertiary-grouped_1fh7w","background-specialbg-secondary-transparent":"bottom-sheet__background-specialbg-secondary-transparent_1fh7w","background-specialbg-secondary-transparent-inverted":"bottom-sheet__background-specialbg-secondary-transparent-inverted_1fh7w","background-specialbg-tertiary-transparent":"bottom-sheet__background-specialbg-tertiary-transparent_1fh7w","background-specialbg-tertiary-transparent-inverted":"bottom-sheet__background-specialbg-tertiary-transparent-inverted_1fh7w"};
require('./index.css');

var isNil = fnUtils.isNil;
var BottomSheet = forwardRef(function (_a, ref) {
    var _b, _c, _d, _e, _f;
    var open = _a.open, title = _a.title, container = _a.container, usePortal = _a.usePortal, backgroundColor = _a.backgroundColor, _g = _a.titleSize, titleSize = _g === void 0 ? 'default' : _g, subtitle = _a.subtitle, actionButton = _a.actionButton, contentClassName = _a.contentClassName, containerClassName = _a.containerClassName, containerProps = _a.containerProps, headerClassName = _a.headerClassName, footerClassName = _a.footerClassName, addonClassName = _a.addonClassName, closerClassName = _a.closerClassName, backerClassName = _a.backerClassName, modalClassName = _a.modalClassName, modalWrapperClassName = _a.modalWrapperClassName, className = _a.className, leftAddons = _a.leftAddons, rightAddons = _a.rightAddons, bottomAddons = _a.bottomAddons, hasCloser = _a.hasCloser, hasBacker = _a.hasBacker, _h = _a.titleAlign, titleAlign = _h === void 0 ? 'left' : _h, trimTitle = _a.trimTitle, stickyHeader = _a.stickyHeader, _j = _a.stickyFooter, stickyFooter = _j === void 0 ? true : _j, _k = _a.initialHeight, initialHeight = _k === void 0 ? 'default' : _k, hideOverlay = _a.hideOverlay, hideScrollbar = _a.hideScrollbar, hideHeader = _a.hideHeader, disableOverlayClick = _a.disableOverlayClick, disableBlockingScroll = _a.disableBlockingScroll, disableFocusLock = _a.disableFocusLock, children = _a.children, zIndex = _a.zIndex, _l = _a.transitionProps, transitionProps = _l === void 0 ? {} : _l, magneticAreasProp = _a.magneticAreas, initialActiveAreaIndex = _a.initialActiveAreaIndex, dataTestId = _a.dataTestId, _m = _a.swipeable, swipeable = _m === void 0 ? true : _m, _o = _a.swipeableContent, swipeableContent = _o === void 0 ? true : _o, _p = _a.swipeThreshold, swipeThreshold = _p === void 0 ? 5 : _p, scrollLockedProp = _a.scrollLocked, backdropProps = _a.backdropProps, _q = _a.scrollableContainerRef, scrollableContainerRef = _q === void 0 ? function () { return null; } : _q, bottomSheetInstanceRef = _a.bottomSheetInstanceRef, _r = _a.sheetContainerRef, sheetContainerRef = _r === void 0 ? function () { return null; } : _r, _s = _a.headerOffset, headerOffset = _s === void 0 ? 24 : _s, onClose = _a.onClose, onBack = _a.onBack, onMagnetize = _a.onMagnetize, onSwipeStart = _a.onSwipeStart, onSwipeEnd = _a.onSwipeEnd, disableRestoreFocus = _a.disableRestoreFocus, disableAutoFocus = _a.disableAutoFocus, disableEscapeKeyDown = _a.disableEscapeKeyDown, keepMounted = _a.keepMounted, onMagnetizeEnd = _a.onMagnetizeEnd, onOffsetChange = _a.onOffsetChange, swipeableMarker = _a.swipeableMarker, swipeableMarkerClassName = _a.swipeableMarkerClassName, backButtonProps = _a.backButtonProps;
    var fullHeight = use100vh() || 0;
    // Хук use100vh рассчитывает высоту вьюпорта в useEffect, поэтому на первый рендер всегда возвращает null.
    var isFirstRender = fullHeight === 0;
    var magneticAreas = useMemo(function () {
        if (magneticAreasProp) {
            return magneticAreasProp.map(function (area) {
                return convertPercentToNumber(area, fullHeight, headerOffset);
            });
        }
        return [0, fullHeight - headerOffset];
    }, [fullHeight, headerOffset, magneticAreasProp]);
    var lastMagneticArea = magneticAreas[magneticAreas.length - 1];
    var _t = useState(0), sheetOffset = _t[0], setSheetOffset = _t[1];
    var _u = useState(1), backdropOpacity = _u[0], setBackdropOpacity = _u[1];
    var _v = useState(-1), activeAreaIdx = _v[0], setActiveAreaIdx = _v[1];
    var _w = useState(null), swipingInProgress = _w[0], setSwipingInProgress = _w[1];
    var scrollOccurred = useRef(false);
    var headerRef = useRef(null);
    var contentRef = useRef(null);
    var sheetHeight = useRef(0);
    var sheetRef = useRef(null);
    var scrollableContainer = useRef(null);
    var activeArea = magneticAreas[activeAreaIdx];
    var emptyHeader = !hasCloser && !leftAddons && !title && !hasBacker && !rightAddons;
    var titleIsReactElement = React.isValidElement(title);
    var headerProps = __assign(__assign({}, (titleIsReactElement
        ? { children: title }
        : { title: title ? title === null || title === void 0 ? void 0 : title.toString() : undefined })), { scrollableParentRef: scrollableContainer, headerRef: headerRef, headerOffset: headerOffset, className: headerClassName, addonClassName: addonClassName, closerClassName: closerClassName, backButtonClassName: backerClassName, leftAddons: leftAddons, rightAddons: rightAddons, bottomAddons: bottomAddons, hasCloser: hasCloser, hasBackButton: hasBacker, align: titleAlign, trim: trimTitle, sticky: stickyHeader, dataTestId: getDataTestId(dataTestId, 'header'), onBack: onBack, titleSize: titleSize, subtitle: subtitle, onClose: onClose, backButtonProps: backButtonProps });
    var startSwiping = function (event) {
        setSwipingInProgress(function (p) {
            if (!p)
                onSwipeStart === null || onSwipeStart === void 0 ? void 0 : onSwipeStart(event);
            return true;
        });
    };
    var stopSwiping = function (event) {
        setSwipingInProgress(function (p) {
            if (p)
                onSwipeEnd === null || onSwipeEnd === void 0 ? void 0 : onSwipeEnd(event);
            return false;
        });
        scrollOccurred.current = false;
    };
    var getBackdropOpacity = function (offset) {
        var canClose = magneticAreas[0] === 0;
        var secondArea = magneticAreas[1];
        var isSecondArea = secondArea === activeArea;
        if (canClose && isSecondArea && sheetOffset > lastMagneticArea - secondArea) {
            var opacity = 1 - (offset - (lastMagneticArea - secondArea)) / secondArea;
            return Math.max(0, Number(opacity.toFixed(2)));
        }
        return 1;
    };
    var getSheetOffset = function (deltaY) {
        var offset = lastMagneticArea - activeArea + deltaY;
        var maxOffset = magneticAreas[0] === 0 ? 0 : lastMagneticArea - magneticAreas[0];
        if (maxOffset) {
            offset = Math.min(maxOffset, offset);
        }
        return Math.max(0, Math.round(offset));
    };
    var getActiveAreaIndex = function (area) { return magneticAreas.findIndex(function (a) { return a === area; }); };
    var setSheetHeight = function () {
        if (sheetRef.current) {
            sheetHeight.current = sheetRef.current.getBoundingClientRect().height - sheetOffset;
        }
    };
    var handleTransitionEnd = function (event) {
        setSheetHeight();
        if (event.propertyName === 'transform' && event.target === event.currentTarget) {
            onMagnetizeEnd === null || onMagnetizeEnd === void 0 ? void 0 : onMagnetizeEnd(getActiveAreaIndex(activeArea));
        }
    };
    var scrollToArea = function (idx) {
        stopSwiping(null);
        var nextArea = magneticAreas[idx];
        var nextAreaIdx = getActiveAreaIndex(nextArea);
        if (nextArea === 0) {
            onClose();
            return;
        }
        if (nextAreaIdx !== -1) {
            setActiveAreaIdx(nextAreaIdx);
            setSheetOffset(lastMagneticArea - nextArea);
            onMagnetize === null || onMagnetize === void 0 ? void 0 : onMagnetize(nextAreaIdx);
        }
    };
    var magnetize = function (velocity, deltaY) {
        var shouldMagnetizeDownByVelocity = deltaY >= 0 && velocity >= SWIPE_VELOCITY;
        var shouldMagnetizeUpByVelocity = deltaY < 0 && velocity >= SWIPE_VELOCITY;
        var updatePosition = function (nextOffset, nextAreaIdx) {
            setSheetOffset(nextOffset);
            setActiveAreaIdx(function (prevState) { return (nextAreaIdx === -1 ? prevState : nextAreaIdx); });
            if (nextAreaIdx !== -1) {
                onMagnetize === null || onMagnetize === void 0 ? void 0 : onMagnetize(nextAreaIdx);
                // Если nextOffset == offset, то onTransitionEnd не отработает и onMagnetizeEnd не вызовется
                if (sheetOffset === nextOffset) {
                    onMagnetizeEnd === null || onMagnetizeEnd === void 0 ? void 0 : onMagnetizeEnd(nextAreaIdx);
                    setSheetHeight();
                }
            }
        };
        if (shouldMagnetizeDownByVelocity) {
            var nextArea = magneticAreas
                .slice()
                .reverse()
                .find(function (area) { return area < activeArea; });
            if (nextArea === 0) {
                onClose();
                return;
            }
            var nextOffset = nextArea
                ? lastMagneticArea - nextArea
                : lastMagneticArea - activeArea;
            var nextAreaIdx = getActiveAreaIndex(nextArea);
            updatePosition(nextOffset, nextAreaIdx);
            return;
        }
        if (shouldMagnetizeUpByVelocity) {
            var nextArea = magneticAreas.find(function (area) { return area > activeArea; });
            var nextAreaIdx = getActiveAreaIndex(nextArea);
            var nextOffset = nextArea ? lastMagneticArea - nextArea : 0;
            updatePosition(nextOffset, nextAreaIdx);
            return;
        }
        var offset = getSheetOffset(deltaY);
        var currentSheetHeight = lastMagneticArea - offset;
        var secondArea = magneticAreas[1];
        var isSecondArea = activeArea === secondArea;
        var canClose = magneticAreas[0] === 0 && deltaY >= 0;
        var shouldCloseByOffset = isSecondArea && canClose && 1 - currentSheetHeight / activeArea > CLOSE_OFFSET;
        if (shouldCloseByOffset) {
            onClose();
            return;
        }
        var nearestArea = magneticAreas.reduceRight(function (res, area) {
            if (Math.abs(area - currentSheetHeight) < res.minOffset) {
                res.minOffset = area - currentSheetHeight;
                res.nearestArea = area;
            }
            return res;
        }, {
            nearestArea: lastMagneticArea,
            minOffset: lastMagneticArea,
        }).nearestArea;
        if (nearestArea === 0) {
            onClose();
        }
        else {
            var nextOffset = lastMagneticArea - nearestArea;
            var nextAreaIdx = getActiveAreaIndex(nearestArea);
            setBackdropOpacity(1);
            updatePosition(nextOffset, nextAreaIdx);
        }
    };
    /**
     * Если контент внутри шторки скроллится - то шторка не должна свайпаться
     * Если шапка внутри шторки зафиксирована - то шторка должна свайпаться только в области шапки
     */
    var shouldSkipSwiping = function (deltaY, startY, event) {
        var _a;
        if (scrollLockedProp || swipingInProgress)
            return false;
        if (!swipeableContent && ((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
            return true;
        }
        var scrollableNode = scrollableContainer.current;
        // Точка верхней границы (y координата)
        var bottomSheetTopY = fullHeight - sheetHeight.current;
        if (!scrollableNode ||
            (!stickyHeader && Math.abs(bottomSheetTopY - startY) <= MARKER_HEIGHT) ||
            (stickyHeader &&
                headerRef.current &&
                Math.abs(bottomSheetTopY - startY) <= headerRef.current.clientHeight)) {
            return false;
        }
        var shouldScroll = deltaY >= 0
            ? scrollableNode.scrollTop > 0
            : scrollableNode.scrollHeight -
                scrollableNode.scrollTop -
                scrollableNode.clientHeight >
                SCROLL_OFFSET;
        if (!scrollOccurred.current && shouldScroll) {
            scrollOccurred.current = true;
        }
        return shouldScroll;
    };
    var handleSheetSwipe = function (_a) {
        var initial = _a.initial, velocity = _a.velocity, deltaY = _a.deltaY, event = _a.event;
        if (scrollOccurred.current || shouldSkipSwiping(deltaY, initial[1], event)) {
            return;
        }
        magnetize(velocity, deltaY);
    };
    var handleSheetSwipeStart = function (_a) {
        var deltaY = _a.deltaY, initial = _a.initial, event = _a.event;
        if (shouldSkipSwiping(deltaY, initial[1], event)) {
            return;
        }
        startSwiping(event);
    };
    var handleTouchEnd = function (_a) {
        var event = _a.event;
        return stopSwiping(event);
    };
    var handleSheetSwiping = function (_a) {
        var initial = _a.initial, deltaY = _a.deltaY, event = _a.event;
        if (scrollOccurred.current || shouldSkipSwiping(deltaY, initial[1], event)) {
            return;
        }
        if (!swipingInProgress) {
            startSwiping(event);
        }
        var offset = getSheetOffset(deltaY);
        var opacity = getBackdropOpacity(offset);
        setSheetOffset(offset);
        setBackdropOpacity(opacity);
    };
    var sheetSwipeableHandlers = useSwipeable({
        onSwipeStart: handleSheetSwipeStart,
        onSwiping: handleSheetSwiping,
        onSwiped: handleSheetSwipe,
        onTouchEndOrOnMouseUp: handleTouchEnd,
        trackMouse: swipeable,
        trackTouch: swipeable,
        delta: swipeThreshold,
    });
    var handleExited = function (node) {
        var idx = initialActiveAreaIndex;
        setBackdropOpacity(1);
        setSheetOffset(isNil(idx) ? magneticAreas[0] : lastMagneticArea - magneticAreas[idx]);
        setActiveAreaIdx(isNil(idx) ? magneticAreas.length - 1 : idx);
        onMagnetizeEnd === null || onMagnetizeEnd === void 0 ? void 0 : onMagnetizeEnd(0);
        if (transitionProps.onExited) {
            transitionProps.onExited(node);
        }
    };
    var handleEnter = function (node, isAppearing) {
        onMagnetize === null || onMagnetize === void 0 ? void 0 : onMagnetize(initialActiveAreaIndex !== null && initialActiveAreaIndex !== void 0 ? initialActiveAreaIndex : magneticAreas.length - 1);
        if (transitionProps.onEnter) {
            transitionProps.onEnter(node, isAppearing);
        }
    };
    var handleExit = function (node) {
        onMagnetize === null || onMagnetize === void 0 ? void 0 : onMagnetize(0);
        if (transitionProps.onExit) {
            transitionProps.onExit(node);
        }
    };
    var handleEntered = function (node, isAppearing) {
        setBackdropOpacity(1);
        setSheetHeight();
        // Ready for swiping
        setSwipingInProgress(false);
        onMagnetizeEnd === null || onMagnetizeEnd === void 0 ? void 0 : onMagnetizeEnd(getActiveAreaIndex(activeArea));
        if (transitionProps.onEntered) {
            transitionProps.onEntered(node, isAppearing);
        }
    };
    useEffect(function () {
        if (!isFirstRender) {
            // Инициализируем стейт только после того, как была рассчитана высота вьюпорта
            if (activeAreaIdx === -1) {
                var idx = initialActiveAreaIndex;
                setSheetOffset(isNil(idx) ? 0 : lastMagneticArea - magneticAreas[idx]);
                setActiveAreaIdx(isNil(idx) ? magneticAreas.length - 1 : idx);
            }
            else {
                setSheetOffset(activeArea ? lastMagneticArea - activeArea : 0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFirstRender, magneticAreas, lastMagneticArea]);
    useEffect(function () {
        if (!sheetRef.current)
            return;
        var maxOffset = fullHeight - headerOffset;
        var offset = open ? sheetOffset : maxOffset;
        var percent = (offset / maxOffset) * 100;
        onOffsetChange === null || onOffsetChange === void 0 ? void 0 : onOffsetChange(offset, percent);
    }, [fullHeight, headerOffset, onOffsetChange, open, sheetOffset]);
    useImperativeHandle(bottomSheetInstanceRef, function () { return ({
        scrollToArea: scrollToArea,
    }); });
    var getSwipeStyles = function () {
        return sheetOffset ? { transform: "translateY(".concat(sheetOffset, "px)") } : {};
    };
    var getHeightStyles = function () { return ({
        height: !isFirstRender && (initialHeight === 'full' || magneticAreasProp)
            ? "".concat(lastMagneticArea, "px")
            : 'unset',
        maxHeight: isFirstRender ? 0 : "".concat(lastMagneticArea, "px"),
    }); };
    var renderMarker = function () {
        if (swipeable) {
            if (swipeableMarker) {
                return (React.createElement("div", { className: cn(styles.marker, swipeableMarkerClassName) }, swipeableMarker));
            }
            return (React.createElement("div", { className: cn(styles.marker, styles.defaultMarker, swipeableMarkerClassName) }));
        }
        return null;
    };
    var bgClassName = backgroundColor && styles["background-".concat(backgroundColor)];
    return (React.createElement(BaseModal, { open: open, ref: ref, container: container, dataTestId: dataTestId, zIndex: zIndex, onClose: onClose, usePortal: usePortal, scrollHandler: scrollableContainer, Backdrop: SwipeableBackdrop, backdropProps: __assign(__assign({}, backdropProps), { className: styles.disabledPointerEvents, opacity: backdropOpacity, opacityTimeout: TIMEOUT, invisible: hideOverlay }), disableBackdropClick: hideOverlay ? true : disableOverlayClick, className: cn(styles.modal, modalClassName), wrapperClassName: cn(modalWrapperClassName, (_b = {},
            _b[styles.disabledPointerEvents] = hideOverlay,
            _b)), disableBlockingScroll: disableBlockingScroll, disableFocusLock: disableFocusLock, transitionProps: __assign(__assign({ appear: true, timeout: TIMEOUT, classNames: styles }, transitionProps), { onExited: handleExited, onEntered: handleEntered, onEnter: handleEnter, onExit: handleExit }), disableAutoFocus: disableAutoFocus, disableEscapeKeyDown: disableEscapeKeyDown, disableRestoreFocus: disableRestoreFocus, keepMounted: keepMounted },
        React.createElement("div", { style: __assign({}, getHeightStyles()), className: cn(styles.wrapper, (_c = {},
                _c[styles.fullscreen] = headerOffset === 0 && sheetOffset === 0,
                _c)), onTransitionEnd: setSheetHeight },
            React.createElement("div", __assign({ className: cn(styles.component, bgClassName, className, (_d = {},
                    _d[styles.withTransition] = swipingInProgress === false,
                    _d)), style: __assign(__assign({}, getSwipeStyles()), getHeightStyles()) }, sheetSwipeableHandlers, { ref: mergeRefs([sheetRef, sheetContainerRef, sheetSwipeableHandlers.ref]), onTransitionEnd: handleTransitionEnd }),
                renderMarker(),
                React.createElement("div", __assign({}, containerProps, { className: cn(styles.scrollableContainer, containerProps === null || containerProps === void 0 ? void 0 : containerProps.className, containerClassName, (_e = {},
                        _e[styles.scrollLocked] = scrollLockedProp || swipingInProgress,
                        _e[styles.hiddenScrollbar] = hideScrollbar,
                        _e)), ref: mergeRefs([scrollableContainer, scrollableContainerRef]) }),
                    !hideHeader && !emptyHeader && React.createElement(Header, __assign({}, headerProps)),
                    React.createElement("div", { ref: contentRef, className: cn(styles.content, contentClassName, (_f = {},
                            _f[styles.noHeader] = hideHeader || emptyHeader,
                            _f[styles.noFooter] = !actionButton,
                            _f)) }, children),
                    actionButton && (React.createElement(Footer, { sticky: stickyFooter, className: cn(bgClassName, footerClassName) }, actionButton)))))));
});

export { BottomSheet };
